import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { Snackbar, Alert, AlertColor, Slide, SlideProps, IconButton, Typography, Box } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

interface Toast {
  id: string;
  message: string;
  severity: AlertColor;
  duration?: number;
  title?: string;
}

interface ToastContextType {
  showToast: (message: string, severity?: AlertColor, options?: { title?: string; duration?: number }) => void;
  showSuccess: (message: string, title?: string) => void;
  showError: (message: string, title?: string) => void;
  showWarning: (message: string, title?: string) => void;
  showInfo: (message: string, title?: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

function SlideTransition(props: SlideProps) {
  return <Slide {...props} direction="up" />;
}

interface ToastProviderProps {
  children: ReactNode;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const showToast = useCallback((
    message: string,
    severity: AlertColor = 'info',
    options?: { title?: string; duration?: number }
  ) => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const newToast: Toast = {
      id,
      message,
      severity,
      duration: options?.duration ?? 4000,
      title: options?.title
    };
    setToasts(prev => [...prev, newToast]);
  }, []);

  const showSuccess = useCallback((message: string, title?: string) => {
    showToast(message, 'success', { title: title || 'Success' });
  }, [showToast]);

  const showError = useCallback((message: string, title?: string) => {
    showToast(message, 'error', { title: title || 'Error', duration: 6000 });
  }, [showToast]);

  const showWarning = useCallback((message: string, title?: string) => {
    showToast(message, 'warning', { title: title || 'Warning' });
  }, [showToast]);

  const showInfo = useCallback((message: string, title?: string) => {
    showToast(message, 'info', { title });
  }, [showToast]);

  const handleClose = useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ showToast, showSuccess, showError, showWarning, showInfo }}>
      {children}
      {toasts.map((toast, index) => (
        <Snackbar
          key={toast.id}
          open={true}
          autoHideDuration={toast.duration}
          onClose={() => handleClose(toast.id)}
          TransitionComponent={SlideTransition}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          sx={{
            bottom: { xs: 24 + index * 80, sm: 24 + index * 80 },
            transition: 'bottom 0.3s ease-in-out'
          }}
        >
          <Alert
            severity={toast.severity}
            variant="filled"
            onClose={() => handleClose(toast.id)}
            action={
              <IconButton
                size="small"
                aria-label="close"
                color="inherit"
                onClick={() => handleClose(toast.id)}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            }
            sx={{
              minWidth: 300,
              maxWidth: 450,
              boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
              borderRadius: 2,
              '& .MuiAlert-icon': {
                fontSize: 24
              },
              '& .MuiAlert-message': {
                width: '100%'
              }
            }}
          >
            <Box>
              {toast.title && (
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
                  {toast.title}
                </Typography>
              )}
              <Typography variant="body2">
                {toast.message}
              </Typography>
            </Box>
          </Alert>
        </Snackbar>
      ))}
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastContextType => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export default ToastContext;
